import React, { useState } from 'react';
import getConfig from 'next/config';
import { Box, Button, Link } from '@mui/material';
import {
  HeaderContainerBeta,
  GroupingBeta,
  HeaderLogInJoinNowBlockBeta,
  LogoBlockBeta,
  MobileDrawerBeta,
  MobileNestedNavBeta,
  TouchMenuButtonBeta,
  useHeaderDataBeta,
  type HeaderDataProviderProps,
  type MobileVisitorHeaderProps,
} from '@care/navigation';

import useTestFlags from '@/components/hooks/useTestFlags';
import useResponsive from '@/components/hooks/useResponsive';
import { TOP_NAV_LINKS } from '@/constants';
import { CtaLocation, CtaText } from '@/types/enums';

const {
  publicRuntimeConfig: { CZEN_GENERAL },
} = getConfig();

const LogInLink = ({
  logAnalyticsEvent,
}: {
  logAnalyticsEvent: HeaderDataProviderProps['logAnalyticsEvent'];
}) => {
  return (
    <Link
      underline="none"
      variant="body1"
      className="CareFocusOutline"
      sx={{
        color: 'text.contrast',
      }}
      href={`${CZEN_GENERAL}${TOP_NAV_LINKS.logIn}`}
      onClick={() => {
        logAnalyticsEvent({
          name: 'CTA Interacted',
          data: {
            cta_text: CtaText.LOG_IN,
            cta_type: 'link',
            cta_location: CtaLocation.VHP_HEADER,
          },
        });
      }}>
      Log in
    </Link>
  );
};

const LogInButton = ({
  logAnalyticsEvent,
}: {
  logAnalyticsEvent: HeaderDataProviderProps['logAnalyticsEvent'];
}) => {
  return (
    <Button
      variant="contained"
      role="link"
      color="secondary"
      size="small"
      sx={{ width: 'auto', px: 2 }}
      component={Link}
      href={`${CZEN_GENERAL}${TOP_NAV_LINKS.logIn}`}
      rel="nofollow"
      onClick={() => {
        logAnalyticsEvent({
          name: 'CTA Interacted',
          data: {
            cta_text: CtaText.LOG_IN,
            cta_type: 'button',
            cta_location: CtaLocation.VHP_HEADER,
          },
        });
      }}>
      Log in
    </Button>
  );
};

const CustomMobileVisitorHeader: React.FC<MobileVisitorHeaderProps> = ({
  transparent,
  variant = 'default',
  slotProps,
  onMenuClick,
  ...rest
}) => {
  const {
    baseUrl,
    findCareMenuLinks,
    findJobsMenuLinks,
    listYourBusinessMenuLinks,
    resourcesMenuLinks,
    logAnalyticsEvent,
  } = useHeaderDataBeta();
  const mobileNavItems = [
    ...(findCareMenuLinks ? [findCareMenuLinks] : []),
    ...(findJobsMenuLinks ? [findJobsMenuLinks] : []),
    ...(listYourBusinessMenuLinks ? [listYourBusinessMenuLinks] : []),
    ...(resourcesMenuLinks ? [resourcesMenuLinks] : []),
  ];
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { isMobile } = useResponsive();
  const { vgnMwCta } = useTestFlags();
  const isLogInCtaTestEnabled = [2, 3].includes(vgnMwCta?.value) && isMobile;

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsDrawerOpen(true);
    onMenuClick?.(event);
    /**
     * Focus the drawer close button when the menu opens
     * Placed in a setTimeout to wait for next tick to ensure the drawer is open
     */
    setTimeout(() => globalThis.document.getElementById('CareMobileDrawerCloseButton')?.focus(), 0);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    // Focus the menu button when the drawer closes
    globalThis.document.getElementById('CareTouchMenuOpenButton')?.focus();
  };

  return (
    <>
      <HeaderContainerBeta transparent={transparent} {...rest}>
        <GroupingBeta>
          <TouchMenuButtonBeta
            variant={variant}
            onClick={handleMenuClick}
            {...slotProps?.touchMenuButton}
          />
          <LogoBlockBeta variant={variant} href={baseUrl} {...slotProps?.logoBlock} />
        </GroupingBeta>
        <GroupingBeta sx={{ alignSelf: 'flex-end' }}>
          {isLogInCtaTestEnabled ? (
            <>
              {vgnMwCta?.value === 2 ? (
                <LogInLink logAnalyticsEvent={logAnalyticsEvent} />
              ) : (
                <LogInButton logAnalyticsEvent={logAnalyticsEvent} />
              )}
            </>
          ) : (
            <HeaderLogInJoinNowBlockBeta variant={variant} />
          )}
        </GroupingBeta>
      </HeaderContainerBeta>
      <MobileDrawerBeta open={isDrawerOpen} onClose={handleDrawerClose}>
        <MobileNestedNavBeta items={mobileNavItems}>
          <Box pt={4} px={3} pb={1}>
            <Button
              component={Link}
              size="large"
              fullWidth
              variant="outlined"
              href={`${CZEN_GENERAL}${TOP_NAV_LINKS.logIn}`}
              onClick={() => {
                logAnalyticsEvent({
                  name: 'CTA Interacted',
                  data: {
                    cta_text: 'Log in',
                    member_type: 'null',
                  },
                });
              }}>
              Log in
            </Button>
            <Button
              component={Link}
              size="large"
              fullWidth
              variant="contained"
              sx={{ mt: 2 }}
              href={`${CZEN_GENERAL}/app/vhp/get-started`}
              onClick={() => {
                logAnalyticsEvent({
                  name: 'CTA Interacted',
                  data: {
                    cta_text: 'Join now',
                    member_type: 'null',
                  },
                });
              }}>
              Join now
            </Button>
          </Box>
        </MobileNestedNavBeta>
      </MobileDrawerBeta>
    </>
  );
};

export default CustomMobileVisitorHeader;
